import {
  FirstStepSignupAssignment,
  FirstStepSignupConfig,
  FirstStepSlotKind,
} from '../types/workshopFirstStepSignup';

/** Presenters → Alternates → Outside (outside appended; does not shift existing indexes). */
export function totalSignupColumns(config: FirstStepSignupConfig): number {
  return (
    config.standard_slot_count +
    config.alternate_slot_count +
    Math.max(0, config.outside_slot_count || 0)
  );
}

export function slotKindForColumn(
  config: FirstStepSignupConfig,
  columnIndex: number
): FirstStepSlotKind {
  if (columnIndex < config.standard_slot_count) return 'standard';
  if (columnIndex < config.standard_slot_count + config.alternate_slot_count) return 'alternate';
  return 'outside';
}

export function columnHeaders(config: FirstStepSignupConfig): string[] {
  const std = Array.from({ length: config.standard_slot_count }, (_, i) =>
    config.standard_slot_count === 1 ? 'Presenter' : `P${i + 1}`
  );
  const alt = Array.from({ length: config.alternate_slot_count }, (_, i) =>
    config.alternate_slot_count === 1 ? 'Alternate' : `A${i + 1}`
  );
  const outsideCount = Math.max(0, config.outside_slot_count || 0);
  const outside = Array.from({ length: outsideCount }, (_, i) =>
    outsideCount === 1 ? 'Outside' : `O${i + 1}`
  );
  return [...std, ...alt, ...outside];
}

export function columnHeaderFull(config: FirstStepSignupConfig, columnIndex: number): string {
  const kind = slotKindForColumn(config, columnIndex);
  if (kind === 'standard') {
    const n = columnIndex + 1;
    return config.standard_slot_count === 1 ? 'Presenter' : `Presenter ${n}`;
  }
  if (kind === 'alternate') {
    const n = columnIndex - config.standard_slot_count + 1;
    return config.alternate_slot_count === 1 ? 'Alternate' : `Alternate ${n}`;
  }
  const n = columnIndex - config.standard_slot_count - config.alternate_slot_count + 1;
  const outsideCount = Math.max(0, config.outside_slot_count || 0);
  return outsideCount === 1 ? 'Outside workshop' : `Outside ${n}`;
}

export type FirstStepSlotBand = {
  kind: FirstStepSlotKind;
  title: string;
  shortTitle: string;
  columnIndexes: number[];
};

/** Grouped bands for Option A layout (skips empty outside when count is 0). */
export function slotBands(config: FirstStepSignupConfig): FirstStepSlotBand[] {
  const bands: FirstStepSlotBand[] = [
    {
      kind: 'standard',
      title: 'In workshop',
      shortTitle: 'Present',
      columnIndexes: Array.from({ length: config.standard_slot_count }, (_, i) => i),
    },
    {
      kind: 'alternate',
      title: 'Alternates',
      shortTitle: 'Alt',
      columnIndexes: Array.from(
        { length: config.alternate_slot_count },
        (_, i) => config.standard_slot_count + i
      ),
    },
  ];
  const outsideCount = Math.max(0, config.outside_slot_count || 0);
  if (outsideCount > 0) {
    const start = config.standard_slot_count + config.alternate_slot_count;
    bands.push({
      kind: 'outside',
      title: 'Outside workshop',
      shortTitle: 'Outside',
      columnIndexes: Array.from({ length: outsideCount }, (_, i) => start + i),
    });
  }
  return bands;
}

export function assignmentAt(
  config: FirstStepSignupConfig,
  weekIndex: number,
  columnIndex: number
): FirstStepSignupAssignment | undefined {
  return config.assignments.find(
    (a) => a.week_index === weekIndex && a.column_index === columnIndex
  );
}

export function myAssignment(
  config: FirstStepSignupConfig,
  viewerUserId?: string
): FirstStepSignupAssignment | undefined {
  return config.assignments.find(
    (a) => a.is_you || (viewerUserId != null && a.user_id === viewerUserId)
  );
}

export function weekPresentationDate(
  config: FirstStepSignupConfig,
  weekIndex: number
): string | null {
  const weekMeta = config.weeks?.find((w) => w.week_index === weekIndex);
  if (weekMeta?.presentation_date) return weekMeta.presentation_date;

  const first = (config.first_presentation_week_date || '').trim();
  if (!first || weekIndex < 1) return null;
  const [y, m, d] = first.split('-').map(Number);
  if (!y || !m || !d) return null;
  const start = new Date(y, m - 1, d);
  if (Number.isNaN(start.getTime())) return null;
  start.setDate(start.getDate() + 7 * (weekIndex - 1));
  const yy = start.getFullYear();
  const mm = String(start.getMonth() + 1).padStart(2, '0');
  const dd = String(start.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function formatPresentationDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function weekLockedForMember(
  config: FirstStepSignupConfig,
  weekIndex: number
): boolean {
  const week = config.weeks?.find((w) => w.week_index === weekIndex);
  if (week) return week.locked_for_member;
  if (config.locked_week_indexes.includes(weekIndex)) return true;
  return false;
}

export function isAlternateSlot(config: FirstStepSignupConfig, columnIndex: number): boolean {
  return slotKindForColumn(config, columnIndex) === 'alternate';
}

export function isOutsideSlot(config: FirstStepSignupConfig, columnIndex: number): boolean {
  return slotKindForColumn(config, columnIndex) === 'outside';
}

function roleLabel(kind: FirstStepSlotKind): string {
  if (kind === 'alternate') return 'alternate';
  if (kind === 'outside') return 'presented outside';
  return 'presenter';
}

export function firstStepSignupCollapsedSummary(
  config: FirstStepSignupConfig | null,
  loading: boolean,
  viewerUserId?: string
): string {
  if (loading) return 'Loading…';
  if (!config) return 'Not open yet';
  if (!config.enabled && config.assignments.length === 0) return 'Not open yet';

  const mine = myAssignment(config, viewerUserId);
  if (!mine) return 'Not signed up yet';

  const role = roleLabel(slotKindForColumn(config, mine.column_index));
  const date = formatPresentationDate(weekPresentationDate(config, mine.week_index));
  if (date) {
    return `Signed up as ${role} for week ${mine.week_index} — ${date}`;
  }
  return `Signed up as ${role} for week ${mine.week_index}`;
}

export function firstStepSignupShouldShow(
  config: FirstStepSignupConfig | null,
  _options?: { canFacilitate?: boolean }
): boolean {
  return !!config?.enabled;
}

export function firstStepSignupStatusLine(
  config: FirstStepSignupConfig,
  viewerUserId?: string
): string | null {
  const mine = myAssignment(config, viewerUserId);
  if (!mine) return null;
  const kind = slotKindForColumn(config, mine.column_index);
  if (kind === 'alternate') return `You are signed up as an alternate for week ${mine.week_index}.`;
  if (kind === 'outside') {
    return `You are signed up as presented outside the workshop for week ${mine.week_index}.`;
  }
  return `You are signed up as a presenter for week ${mine.week_index}.`;
}
